import React from "react";
import { Button, Card, CardBody, Col, Form, FormGroup, Input, Label, Row, Table } from "reactstrap";
import { BoletaForm, BancoReferencia, CuentaBancoReferencia } from "../types";

interface BoletasStepProps {
  nuevaBoleta: BoletaForm;
  boletas: BoletaForm[];
  totalBoletas: number;
  onNuevaBoletaChange: (campo: keyof BoletaForm, valor: string | number) => void;
  onAgregarBoleta: () => void;
  onEliminarBoleta: (id: number) => void;
}

const BoletasStep = ({
  nuevaBoleta,
  boletas,
  totalBoletas,
  onNuevaBoletaChange,
  onAgregarBoleta,
  onEliminarBoleta,
  bancos,
  cuentas,
  onBancoChange,
  cargandoBancos,
  cargandoCuentaIds,
}: BoletasStepProps & {
  bancos: BancoReferencia[];
  cuentas: Record<number, CuentaBancoReferencia[]>;
  onBancoChange: (id: number) => void;
  cargandoBancos?: boolean;
  cargandoCuentaIds?: Record<number, boolean>;
}) => {
  const puedeGuardarBoleta =
    Boolean(nuevaBoleta.fecha) &&
    Boolean(nuevaBoleta.numero?.trim()) &&
    Boolean(nuevaBoleta.banco) &&
    Boolean(nuevaBoleta.cuenta) &&
    nuevaBoleta.monto > 0;

  const obtenerNombreBanco = (bancoId: string) => {
    if (!bancoId) {
      return "-";
    }
    const encontrado = bancos.find((banco) => String(banco.id) === String(bancoId));
    return encontrado?.descripcion ?? `Banco ${bancoId}`;
  };

  const obtenerCuentaSeleccionada = (boleta: BoletaForm) => {
    if (!boleta.banco) {
      return null;
    }
    const bancoIdNum = Number(boleta.banco);
    if (!Number.isFinite(bancoIdNum)) {
      return null;
    }
    const lista = cuentas[bancoIdNum] ?? [];
    return (
      lista.find((cuenta) => {
        const valorCuenta = cuenta.numero ?? String(cuenta.id);
        return valorCuenta === boleta.cuenta;
      }) ?? null
    );
  };

  return (
    <Row className="g-4">
      <Col xl="5">
        <Card className="h-100">
          <CardBody>
            <h5 className="mb-3">Agregar boleta</h5>
            <Form
              onSubmit={(event) => {
                event.preventDefault();
                if (puedeGuardarBoleta) {
                  onAgregarBoleta();
                }
              }}
            >
              <FormGroup className="mb-3">
                <Label for="boletaFecha">Fecha</Label>
                <Input
                  id="boletaFecha"
                  type="date"
                  value={nuevaBoleta.fecha}
                  onChange={(event) => onNuevaBoletaChange("fecha", event.target.value)}
                  required
                />
              </FormGroup>
              <FormGroup className="mb-3">
                <Label for="boletaNumero">Número / Referencia</Label>
                <Input
                  id="boletaNumero"
                  value={nuevaBoleta.numero}
                  onChange={(event) => onNuevaBoletaChange("numero", event.target.value)}
                  required
                />
              </FormGroup>
              <FormGroup className="mb-3">
                <Label for="boletaBanco">Banco</Label>
                <Input
                  id="boletaBanco"
                  type="select"
                  value={nuevaBoleta.banco ? String(nuevaBoleta.banco) : ""}
                  onChange={(event) => {
                    const idSeleccionado = Number(event.target.value);
                    onBancoChange(Number.isFinite(idSeleccionado) ? idSeleccionado : 0);
                  }}
                  required
                >
                  <option value="">Seleccioná un banco</option>
                  {cargandoBancos && <option value="">Cargando bancos...</option>}
                  {!cargandoBancos &&
                    bancos.map((banco) => (
                      <option key={banco.id} value={String(banco.id)}>
                        {banco.descripcion ?? `Banco ${banco.id}`}
                      </option>
                    ))}
                </Input>
              </FormGroup>
              <FormGroup className="mb-3">
                <Label for="boletaCuenta">Cuenta</Label>
                <Input
                  id="boletaCuenta"
                  type="select"
                  value={nuevaBoleta.cuenta}
                  onChange={(event) => onNuevaBoletaChange("cuenta", event.target.value)}
                  disabled={!nuevaBoleta.banco || !cuentas[Number(nuevaBoleta.banco)]}
                  required
                >
                  <option value="">Seleccioná una cuenta</option>
                  {nuevaBoleta.banco && cargandoCuentaIds?.[Number(nuevaBoleta.banco)] && (
                    <option value="">Cargando cuentas...</option>
                  )}
                  {nuevaBoleta.banco && !cargandoCuentaIds?.[Number(nuevaBoleta.banco)] &&
                    (cuentas[Number(nuevaBoleta.banco)] ?? []).map((cuenta) => (
                      <option key={cuenta.id} value={cuenta.numero ?? String(cuenta.id)}>
                        {cuenta.numero ?? `Cuenta ${cuenta.id}`}
                      </option>
                    ))}
                </Input>
              </FormGroup>
              <FormGroup className="mb-3">
                <Label for="boletaMonto">Monto (Gs)</Label>
                <Input
                  id="boletaMonto"
                  type="number"
                  min="0"
                  value={nuevaBoleta.monto}
                  onChange={(event) => onNuevaBoletaChange("monto", Number(event.target.value))}
                  required
                />
              </FormGroup>
              <Button color="primary" type="submit" disabled={!puedeGuardarBoleta}>
                Agregar boleta
              </Button>
            </Form>
          </CardBody>
        </Card>
      </Col>
      <Col xl="7">
        <Card className="h-100">
          <CardBody>
            <h5 className="mb-3">Boletas registradas</h5>
            {boletas.length === 0 ? (
              <p className="text-muted mb-0">No informaste boletas todavía.</p>
            ) : (
              <div className="table-responsive">
                <Table className="align-middle">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Referencia</th>
                      <th>Banco</th>
                      <th>Cuenta</th>
                      <th>ID caja</th>
                      <th>Monto</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {boletas.map((boleta) => {
                      const cuentaSeleccionada = obtenerCuentaSeleccionada(boleta);
                      const cuentaTexto = cuentaSeleccionada?.numero ?? boleta.cuenta;
                      return (
                        <tr key={boleta.id}>
                          <td>{boleta.fecha || "-"}</td>
                          <td>{boleta.numero}</td>
                          <td>{obtenerNombreBanco(boleta.banco)}</td>
                          <td>{cuentaTexto || "-"}</td>
                          <td>{cuentaSeleccionada?.idcaja ?? "-"}</td>
                          <td>Gs. {boleta.monto.toLocaleString("es-PY")}</td>
                          <td className="text-end">
                            <Button color="link" className="text-danger" onClick={() => onEliminarBoleta(boleta.id)}>
                              Quitar
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                  <tr>
                    <th colSpan={5}>Total</th>
                    <th>Gs. {totalBoletas.toLocaleString("es-PY")}</th>
                    <th></th>
                  </tr>
                </tfoot>
              </Table>
            </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default BoletasStep;
